import AbstractComponent from "../core/abstract-component.js";
import BaseComponent from "../core/base-component.js";

const DEFAULT_ELEMENT_SELECTOR: string = 'iframe';

interface BodyDimension {
    offsetWidth: number;
    offsetHeight: number;
    scrollWidth: number;
    scrollHeight: number;
    marginTop: string;
    marginBottom: string;
    marginLeft: string;
    marginRight: string;
}

interface JsonResponse {
    command: string;
    message?: string;
    data?: any;
    debug?: any;
}

interface JsonRequest extends JsonResponse {}

interface IframeResizeOptions {
    /**
     * Override the body background style in the iFrame.
     */
    bodyBackground?: string | undefined

    /**
     * Override the default body margin style in the iFrame. A string can be any valid value for the
     * CSS margin attribute, for example '8px 3em'. A number value is converted into px.
     */
    bodyMargin?: number | string | undefined

    /**
     * Override the default body padding style in the iFrame. A string can be any valid value for the
     * CSS margin attribute, for example '8px 3em'. A number value is converted into px.
     */
    bodyPadding?: number | string | undefined

    /**
     * When set to true, only allow incoming messages from the domain listed in the src property of the iFrame tag.
     * If your iFrame navigates between different domains, ports or protocols; then you will need to
     * provide an array of URLs or disable this option.
     */
    checkOrigin?: boolean | string[] | undefined

    /**
     * Set the reszing direction of the iframe.
     */
    direction?: 'vertical' | 'horizontal' | 'none' | undefined

    /**
     * Set offset width of iFrame content.
     */
    offsetWidth?: number | undefined

    /**
     * Set offset height of iFrame content.
     */
    offsetHeight?: number | undefined

    /**
     * Enable scroll bars in iFrame.
     */
    scrolling?: boolean | 'auto' | undefined

    /**
     * Set the number of pixels the iFrame content size has to change by, before triggering a resize of the iFrame.
     */
    tolerance?: number | undefined

    waitForLoad?: boolean | undefined
}

const defaultOptions: IframeResizeOptions = {
    bodyBackground: '',
    bodyMargin: '',
    bodyPadding: '',
    checkOrigin: false,
    direction: 'none',
    offsetWidth: 0,
    offsetHeight: 0,
    tolerance: 0,
    scrolling: undefined,
    waitForLoad: false
};


class IframeResizeParent extends BaseComponent {
    protected options: IframeResizeOptions = defaultOptions;

    public override init() {
        this.options = this.assignConfig<IframeResizeOptions>(defaultOptions, this.config, undefined, false);
        const iframeEl = this.element as HTMLIFrameElement;

        if(this.options.direction && this.options.direction == 'none') {
            this.options.direction = 'vertical';
        }

        if(typeof(this.options.scrolling) != undefined) {
            if(this.options.scrolling == 'auto') {
                iframeEl.setAttribute('scrolling', 'auto');
                iframeEl.style.setProperty('overflow', 'auto');
            } else if(this.options.scrolling) {
                iframeEl.setAttribute('scrolling', 'yes');
                iframeEl.style.setProperty('overflow', 'scroll');
            } else {
                iframeEl.setAttribute('scrolling', 'no');
                iframeEl.style.setProperty('overflow', 'hidden');
            }
        }


        if(this.options.offsetWidth && this.options.offsetWidth > 0) {
            iframeEl.style.setProperty('width', this.options.offsetWidth+'px');
        }
        if(this.options.offsetHeight && this.options.offsetHeight > 0) {
            iframeEl.style.setProperty('height', this.options.offsetHeight+'px');
        }

    }

    public override process() {
        const iframeEl = this.element as HTMLIFrameElement;
        if(
            typeof(this.options.direction) == 'undefined' || this.options.direction == 'none'
            && !iframeEl.classList.contains('autoresize')
            && iframeEl.getAttribute('autoresize') == null
        ) {
            return;
        }

        window.addEventListener('message', (ev: MessageEvent) => {
            if(
                ev.data
                && typeof(ev.data) == 'string'
                && ev.source == iframeEl.contentWindow
            ) {
                this.handleMessage(ev.data);
            }
        });
    }

    protected handleMessage(data: string) {
        const json: JsonResponse = JSON.parse(data);
        if(typeof(json.command) != undefined) {
            switch(json.command) {
                case 'init':
                    this.postMessage({
                        command: 'parentFrameInizialized',
                        data: {
                            options: this.options
                        }
                    });
                break;
                case 'clientSizeChanged':
                    const bodyDimension = json.data as BodyDimension;
                    this.resizeFrame(bodyDimension);
                break;
            }
        }
    }

    protected resizeFrame(bodyDimension: BodyDimension) {
        let resize = true;
        const iframeEl = this.element as HTMLIFrameElement;
        if(this.options.tolerance) {
            resize = false;
            const tolerance = this.options.tolerance;
            if(this.options.direction == 'horizontal') {
                if((iframeEl.offsetWidth + tolerance) < bodyDimension.scrollWidth) {
                    resize = true;
                }
            } else if(this.options.direction == 'vertical') {
                if((iframeEl.offsetHeight + tolerance) < bodyDimension.scrollHeight) {
                    resize = true;
                }
            }
        }

        if(resize) {
            iframeEl.style.height = bodyDimension.scrollHeight+"px";
            iframeEl.style.overflowY = 'auto';
            this.postMessage({ command: 'parentFrameResized', data: {
                offsetWidth: iframeEl.offsetWidth,
                offsetHeight: iframeEl.offsetHeight
            }});
        }

    }

    protected postMessage(json: JsonRequest) {
        const iframeEl = this.element as HTMLIFrameElement;
        if(iframeEl.contentWindow) {
            iframeEl.contentWindow.postMessage(JSON.stringify(json));
        }
    }

}

class IframeResizeClient extends AbstractComponent {
    protected options: IframeResizeOptions = defaultOptions;
    protected lastScrollHeight: number = 0;
    protected updateTimeOut: number = 0;
    protected parentIframeOffsetWidth: number = 0;
    protected parentIframeOffsetHeight: number = 0;
    protected isInitialized: boolean = false;

    constructor() {
        super();
        if(window.self !== window.parent) {
            window.addEventListener('message', (ev: MessageEvent) => {
                if(ev.source == window.parent && ev.data) {
                    this.handleMessage(ev.data);
                }
            });

            this.postMessage({ command: 'init' });
        }
    }

    protected handleMessage(data: string) {
        const json: JsonResponse = JSON.parse(data);
        if(typeof(json.command) != undefined) {
            switch(json.command) {
                case 'parentFrameInizialized':
                    this.options = json.data.options;
                    this.updateFrameBody();
                    this.initReszing();
                    this.isInitialized = true;
                break;
                case 'parentFrameResized':
                    this.parentIframeOffsetWidth = json.data.offsetWidth;
                    this.parentIframeOffsetHeight = json.data.offsetHeight;
                break;
            }
        }
    }

    protected initReszing() {
        window.addEventListener('load', () => {
            this.postUpdateMessage();
        });

        if(!defaultOptions.waitForLoad) {
            this.postUpdateMessage();
        }

        const observer = new MutationObserver((mutationList: MutationRecord[], observer: MutationObserver) => {
            for(const mutation of mutationList) {
                if(mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach((el: Node) => {
                        if(
                            el instanceof HTMLImageElement
                            || el instanceof HTMLIFrameElement
                            || el instanceof HTMLMediaElement
                        ) {
                            let addLoadEvent = true;
                            if(el instanceof HTMLImageElement && el.complete) {
                                addLoadEvent = false;
                            }
                            if(addLoadEvent) {
                                el.addEventListener('load', () => {
                                    this.postUpdateMessage();
                                });
                            }
                        }
                    });
                }

                if(mutation.type == 'childList') {
                    this.postUpdateMessage();
                }
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        window.addEventListener('resize', () => {
            this.postUpdateMessage();
        });
        window.addEventListener('orientationchange', () => {
            this.postUpdateMessage();
        });
    }

    protected updateFrameBody() {
        this.setBodyStyle('background', this.options.bodyBackground);
        this.setBodyStyle('margin', this.options.bodyMargin?.toString());
    }

    protected setBodyStyle(attr: string, value: string | undefined) {
        if(undefined !== value && value !== '' && value !== 'null') {
            document.body.style.setProperty(attr, value);
        }
    }

    protected postMessage(json: JsonRequest) {
        if(window.parent && window.self != window.parent) {
            window.parent.postMessage(JSON.stringify(json));
        }
    }

    protected postUpdateMessage() {
        window.clearTimeout(this.updateTimeOut);
        this.updateTimeOut = window.setTimeout(() => {
            const bodyDimension: BodyDimension = {
                offsetWidth: document.body.offsetWidth,
                offsetHeight: document.body.offsetHeight,
                scrollWidth: document.body.scrollWidth,
                scrollHeight: document.body.scrollHeight,
                marginTop: document.body.style.marginTop,
                marginBottom: document.body.style.marginBottom,
                marginLeft: document.body.style.marginLeft,
                marginRight: document.body.style.marginRight
            };

            this.postMessage({
                command: 'clientSizeChanged',
                data: bodyDimension
            });
        }, 100);
    }
}

IframeResizeParent.initializeDom(DEFAULT_ELEMENT_SELECTOR, true, false, true);

const iframeResizeClient = new IframeResizeClient();

export { IframeResizeParent, IframeResizeClient, type IframeResizeOptions, defaultOptions as iframeResizeDefaultOptions, iframeResizeClient };
